import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, IconButton, Paper, Typography, Modal } from '@mui/material';
import { PhotoCamera, Cameraswitch, Close, Check } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';

/**
 * Componente reutilizable para captura de fotos inline
 * 
 * @param {boolean} open - Controla si el modal está abierto
 * @param {Function} onPhotoConfirm - Callback que recibe el archivo de la foto cuando se confirma
 * @param {Function} onClose - Callback para cerrar el componente
 */
const InlinePhotoCapture = ({ open, onPhotoConfirm, onClose }) => {
  const { t } = useTranslation();
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' for front camera, 'environment' for back
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Iniciar cámara
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error al acceder a la cámara:', error);
      alert(t('camera.cameraError'));
    }
  };

  // Detener cámara
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  // Cerrar modal
  const handleClose = () => {
    stopCamera();
    setCapturedImage(null);
    setCapturedFile(null);
    if (onClose) onClose();
  };

  // Capturar foto
  const handleCapture = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], `${uuidv4()}.jpg`, { type: 'image/jpeg' });
        const url = URL.createObjectURL(blob);
        
        setCapturedImage(url);
        setCapturedFile(file);
        stopCamera();
      }, 'image/jpeg', 0.9);
    }
  };

  // Retomar foto
  const handleRetake = () => {
    setCapturedImage(null);
    setCapturedFile(null);
    startCamera();
  };

  // Confirmar foto
  const handleConfirm = () => {
    if (capturedFile && onPhotoConfirm) {
      onPhotoConfirm(capturedFile, capturedImage);
    }
    handleClose();
  };

  // Cambiar cámara (frontal/trasera)
  const handleSwitchCamera = async () => {
    stopCamera();
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
  };

  // Iniciar cámara cuando se abre el modal
  useEffect(() => {
    if (open && !capturedImage) {
      startCamera();
    }
    
    return () => {
      stopCamera();
    };
  }, [open, facingMode]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          position: 'relative',
          p: 2,
          backgroundColor: '#000',
          borderRadius: 2,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
          outline: 'none'
        }}
      >
        {/* Botón cerrar */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 10,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.7)',
            }
          }}
        >
          <Close />
        </IconButton>

        <Box sx={{ position: 'relative', width: '100%', maxWidth: 600, mx: 'auto' }}>
        {!capturedImage ? (
          <>
            {/* Video preview */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                maxHeight: '60vh',
                borderRadius: 8,
                backgroundColor: '#000',
                display: 'block'
              }}
            />
            
            {/* Controles de cámara */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 2, 
              mt: 2 
            }}>
              <Button
                variant="contained"
                onClick={handleSwitchCamera}
                startIcon={<Cameraswitch />}
                color="secondary"
              >
                {t('camera.switchCamera')}
              </Button>
              <Button
                variant="contained"
                onClick={handleCapture}
                startIcon={<PhotoCamera />}
                size="large"
                color="primary"
              >
                {t('camera.capture')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            {/* Imagen capturada */}
            <img
              src={capturedImage}
              alt="Captured"
              style={{
                width: '100%',
                maxHeight: '60vh',
                borderRadius: 8,
                display: 'block'
              }}
            />
            
            {/* Controles de confirmación */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 2, 
              mt: 2 
            }}>
              <Button
                variant="outlined"
                onClick={handleRetake}
                color="error"
              >
                {t('camera.retake')}
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                startIcon={<Check />}
                size="large"
                color="success"
              >
                {t('camera.confirmCapture')}
              </Button>
            </Box>
          </>
        )}
        </Box>

        {/* Canvas oculto para captura */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Paper>
    </Modal>
  );
};

export default InlinePhotoCapture;

